import React, { useState, useEffect } from "react";
import { BiX } from "react-icons/bi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useAppDispatch } from "../../redux/hooks";
import { addWarehouseToState } from "../../redux/thunk";

const PRIMARY_COLOR = "#008B50";
const PRIMARY_HOVER_COLOR = "#007A48";
const PRIMARY_RING_COLOR = "focus:ring-[#008B50]";
const PRIMARY_BORDER_COLOR = "focus:border-[#008B50]";

interface AddStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  stateId: string | null | undefined;
}

export default function AddStoreToStateModal({
  isOpen,
  onClose,
  stateId,
}: AddStoreModalProps) {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState({
    storeName: "",
    location: "",
    contactInfo: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({ storeName: "", location: "", contactInfo: "" });
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stateId) return;
    setLoading(true);
    setError(null);
    try {
      const resultAction = await dispatch(
        addWarehouseToState({ stateId, data: formData })
      );
      if (addWarehouseToState.fulfilled.match(resultAction)) {
        onClose();
      } else {
        const errorMsg =
          (resultAction.payload as string) || "Failed to add warehouse.";
        setError(errorMsg);
      }
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out"
      onClick={onClose}
      aria-labelledby="add-store-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 md:p-8 max-w-lg w-full relative transform transition-all duration-300 ease-in-out scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out"
          aria-label="Close modal"
        >
          <BiX size={26} />
        </button>

        <h2
          id="add-store-modal-title"
          className="text-xl font-semibold text-gray-800 mb-6"
        >
          Add New Warehouse to State
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div>
              <label
                htmlFor="add-storeName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Warehouse/Store Name
              </label>
              <input
                type="text"
                id="add-storeName"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                placeholder="Enter name"
                required
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              />
            </div>

            <div>
              <label
                htmlFor="add-location"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Location
              </label>
              <input
                type="text"
                id="add-location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Enter location (e.g., City, Area)"
                required
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              />
            </div>

            <div>
              <label
                htmlFor="add-contactInfo"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contact Info (Phone)
              </label>
              <input
                type="text"
                id="add-contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                placeholder="Enter phone number"
                required
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none ${PRIMARY_RING_COLOR} ${PRIMARY_BORDER_COLOR} sm:text-sm`}
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 text-center text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`inline-flex justify-center px-5 py-2 text-sm font-medium text-white bg-[${PRIMARY_COLOR}] border border-transparent rounded-md shadow-sm hover:bg-[${PRIMARY_HOVER_COLOR}] focus:outline-none focus:ring-2 focus:ring-offset-2 ${PRIMARY_RING_COLOR} transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin h-5 w-5" />
              ) : (
                "Add Warehouse"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
